/**
 * Businesses hooks (CRUD + AI field-assist SSE + AI scan).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApiClient } from '../context';
import { ApiError, getClientBaseUrl, getClientFetch, parseApiError } from '../hc';
import { deleteJson, getJson, patchJson, postJson } from './_http';

/* ─── Businesses — Wave 5b Agent 5b.2 (EPIC-09 US-104..US-119) ─────────── */

/**
 * `Business` — server response shape for `/businesses` endpoints.
 *
 * We mirror the DB row (timestamps come back as ISO strings on the wire,
 * Date objects when the API serialises directly through Drizzle/D1; both
 * shapes are accepted via `string | number`). The schema definitions in
 * `@barel/core/schemas/business` carry the canonical field set; we
 * keep this declaration in sync without importing zod into the api-client
 * surface (the extension consumes this barrel and shouldn't pay for zod).
 */
export interface Business {
  id: string;
  userId: string;
  linkedinAccountId: string | null;
  name: string;
  description: string;
  website: string;
  socials: { kind: string; label: string; url: string }[];
  email: string;
  phone: string;
  calendarUrl: string;
  contactPriority: 'calendar_first' | 'phone_first' | 'calendar_only' | 'phone_only';
  personas: { title: string; desc: string }[];
  pains: string[];
  valueProps: string[];
  hotSignals: string[];
  businessNature: 'service' | 'product' | 'both' | '';
  pricingModels: string[];
  pricingProducts: { name: string; price: string; note: string }[];
  pricingPolicy: 'on_demand' | 'open' | 'never';
  questionGuide: string[];
  bannedWords: string[];
  salesRules: string[];
  /** Wave C2 — JSON blob with the last `BUSINESS_SCAN` suggestion. */
  lastScanSuggestion: BusinessScanSuggestion | null;
  lastScanRunId: string | null;
  createdAt: string | number;
  updatedAt: string | number;
  archivedAt: string | number | null;
}

/** Business row enriched by the authoritative list-endpoint aggregation. */
export interface BusinessListItem extends Business {
  campaignCount: number;
}

/** Subset of `Business` the LLM emits as a suggestion (US-115). */
export interface BusinessScanSuggestion {
  description?: string;
  personas?: { title: string; desc: string }[];
  pains?: string[];
  valueProps?: string[];
  hotSignals?: string[];
  questionGuide?: string[];
  salesRules?: string[];
}

export type BusinessScanField = keyof BusinessScanSuggestion;

export interface BusinessAiScanInput {
  website?: string;
  linkedinCompany?: string;
  linkedinProfile?: string;
  additionalContext?: string;
  readHistory?: boolean;
}

export interface BusinessAiScanAck {
  runId: string;
  acknowledgedAt: number;
  state: 'running';
  sources: BusinessAiScanInput;
}

export interface BusinessImpact {
  leadsCount: number;
  activeCampaignsCount: number;
  draftCampaignsCount: number;
  pendingAiDraftsCount: number;
}

export type BusinessAiFieldCurrent = string | string[] | { title: string; desc: string }[];
export type BusinessAiFieldValue = string | string[] | { title: string; desc: string }[];

/**
 * Bounded business context sent with a field-assist request.
 *
 * This is deliberately smaller than `Business`: server metadata, account
 * binding and scan state are neither useful to the prompt nor writable by
 * this action. The API validates the snapshot with the shared business
 * schemas before it reaches the model.
 */
export interface BusinessAiFieldProfile {
  name: string;
  description: string;
  website: string;
  personas: { title: string; desc: string }[];
  pains: string[];
  valueProps: string[];
  hotSignals: string[];
  businessNature: Business['businessNature'];
  pricingModels: string[];
  pricingProducts: { name: string; price: string; note: string }[];
  pricingPolicy: Business['pricingPolicy'];
  questionGuide: string[];
  bannedWords: string[];
  salesRules: string[];
}

export interface BusinessAiFieldError {
  code: string;
  i18nKey: string;
  httpStatus: number;
  retryable: boolean;
  message: string;
  correlationId: string;
  details?: unknown;
}

export interface BusinessAiFieldFrame {
  field: string;
  delta?: string;
  done?: boolean;
  value?: BusinessAiFieldValue;
  error?: BusinessAiFieldError;
}

/** `GET /api/v1/businesses` — list (non-archived). */
export function useBusinesses(
  options: { enabled?: boolean } = {},
): UseQueryResult<{ businesses: BusinessListItem[] }, ApiError> {
  const client = useApiClient();
  return useQuery<{ businesses: BusinessListItem[] }, ApiError>({
    queryKey: ['businesses'],
    queryFn: () => getJson<{ businesses: BusinessListItem[] }>(client, '/api/v1/businesses'),
    enabled: options.enabled,
    retry: false,
  });
}

/** `GET /api/v1/businesses/:id` — single business. */
export function useBusiness(
  id: string | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<{ business: Business }, ApiError> {
  const client = useApiClient();
  const enabled = options.enabled ?? Boolean(id);
  return useQuery<{ business: Business }, ApiError>({
    queryKey: ['business', id],
    queryFn: () =>
      getJson<{ business: Business }>(client, `/api/v1/businesses/${encodeURIComponent(id ?? '')}`),
    enabled,
    retry: false,
  });
}

/** `POST /api/v1/businesses` — create with mandatory `linkedinAccountId` (US-119). */
export function useCreateBusiness(): UseMutationResult<
  { business: Business },
  ApiError,
  Partial<Omit<Business, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'archivedAt'>> & {
    name: string;
    linkedinAccountId: string;
  }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    { business: Business },
    ApiError,
    Partial<Omit<Business, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'archivedAt'>> & {
      name: string;
      linkedinAccountId: string;
    }
  >({
    mutationFn: (vars) => postJson<{ business: Business }>(client, '/api/v1/businesses', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['businesses'] });
    },
  });
}

/** `PATCH /api/v1/businesses/:id` — partial update. */
export function useUpdateBusiness(): UseMutationResult<
  { business: Business },
  ApiError,
  { id: string; patch: Partial<Business> }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ business: Business }, ApiError, { id: string; patch: Partial<Business> }>({
    mutationFn: (vars) =>
      patchJson<{ business: Business }>(
        client,
        `/api/v1/businesses/${encodeURIComponent(vars.id)}`,
        vars.patch,
      ),
    onSuccess: (data, vars) => {
      qc.setQueryData(['business', vars.id], data);
      void qc.invalidateQueries({ queryKey: ['businesses'] });
    },
  });
}

/** `DELETE /api/v1/businesses/:id` — soft archive (US-118 cascade). */
export function useDeleteBusiness(): UseMutationResult<{ ok: true }, ApiError, { id: string }> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { id: string }>({
    mutationFn: (vars) =>
      deleteJson<{ ok: true }>(client, `/api/v1/businesses/${encodeURIComponent(vars.id)}`),
    onSuccess: (_data, vars) => {
      qc.removeQueries({ queryKey: ['business', vars.id] });
      void qc.invalidateQueries({ queryKey: ['businesses'] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}

/**
 * `POST /api/v1/businesses/:id/ai-field` — SSE per-field AI assist (US-113).
 *
 * Returns an async-iterator factory: callers pass `{ id, field, current }`
 * and receive an `AsyncIterable<string>` whose values are the incremental
 * deltas. The stream completes when the server emits a `done: true` frame
 * (or on `AbortSignal` cancellation).
 *
 * We don't expose this via `useMutation` because the live-streaming
 * semantics don't match the single-shot mutation lifecycle. Consumers
 * call the imperative function from inside `useState` / `useEffect`.
 */
export function useBusinessAiField(): {
  open: (params: {
    id: string;
    field: string;
    current?: BusinessAiFieldCurrent;
    profile?: BusinessAiFieldProfile;
    signal?: AbortSignal;
  }) => Promise<AsyncIterable<BusinessAiFieldFrame>>;
} {
  const client = useApiClient();
  return {
    open: async ({ id, field, current, profile, signal }) => {
      const baseUrl = getClientBaseUrl(client);
      const wrappedFetch = getClientFetch(client);
      const response = await wrappedFetch(
        `${baseUrl}/api/v1/businesses/${encodeURIComponent(id)}/ai-field`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
          body: JSON.stringify({ field, current, profile }),
          signal,
        },
      );
      if (!response.ok) {
        throw await parseApiError(response);
      }
      if (!response.body) {
        throw new ApiError({
          code: 'internal_error',
          httpStatus: 500,
          i18nKey: 'errors.internal_error',
          message: 'response body missing',
        });
      }
      return iterateSseFrames(response.body, signal);
    },
  };
}

async function* iterateSseFrames(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncIterable<BusinessAiFieldFrame> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    while (true) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let frameEnd = buffer.indexOf('\n\n');
      while (frameEnd !== -1) {
        const frame = buffer.slice(0, frameEnd);
        buffer = buffer.slice(frameEnd + 2);
        const parsed = parseBusinessAiFieldFrame(frame);
        if (parsed) yield parsed;
        frameEnd = buffer.indexOf('\n\n');
      }
    }
    if (buffer.length > 0) {
      const parsed = parseBusinessAiFieldFrame(buffer);
      if (parsed) yield parsed;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }
}

export function parseBusinessAiFieldFrame(frame: string): BusinessAiFieldFrame | null {
  let event = '';
  let data = '';
  for (const line of frame.split('\n')) {
    if (line.startsWith(':') || line.length === 0) continue;
    if (line.startsWith('event:')) event = line.slice(6).trim();
    if (line.startsWith('data:')) {
      data += line.slice(5).trim();
    }
  }
  if (!data) return null;
  try {
    const parsed = JSON.parse(data) as BusinessAiFieldFrame;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.field !== 'string') return null;
    if (event === 'error' && !parsed.error) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** `POST /api/v1/businesses/:id/ai-scan` — kicks off the scan workflow (US-114..US-116). */
export function useBusinessAiScan(): UseMutationResult<
  BusinessAiScanAck,
  ApiError,
  { id: string; input: BusinessAiScanInput }
> {
  const client = useApiClient();
  return useMutation<BusinessAiScanAck, ApiError, { id: string; input: BusinessAiScanInput }>({
    mutationFn: (vars) =>
      postJson<BusinessAiScanAck>(
        client,
        `/api/v1/businesses/${encodeURIComponent(vars.id)}/ai-scan`,
        vars.input,
      ),
  });
}

/**
 * `POST /api/v1/businesses/:id/ai-scan/:runId/apply` — Wave C2 (US-116).
 *
 * Applies a subset of the persisted `lastScanSuggestion` to the live
 * business row. The `runId` must match the row's `last_scan_run_id` to
 * prevent cross-run replay (the workflow refreshes both atomically).
 */
export function useApplyBusinessScanSuggestions(): UseMutationResult<
  { business: Business; applied: BusinessScanField[] },
  ApiError,
  { id: string; runId: string; acceptedFields: BusinessScanField[] }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    { business: Business; applied: BusinessScanField[] },
    ApiError,
    { id: string; runId: string; acceptedFields: BusinessScanField[] }
  >({
    mutationFn: async (vars) => {
      const result = await postJson<{ business: Business; applied: BusinessScanField[] }>(
        client,
        `/api/v1/businesses/${encodeURIComponent(vars.id)}/ai-scan/${encodeURIComponent(
          vars.runId,
        )}/apply`,
        { acceptedFields: vars.acceptedFields },
      );
      const applied = Array.isArray(result?.applied) ? result.applied : [];
      const missingFields = vars.acceptedFields.filter((field) => !applied.includes(field));
      if (!result?.business || missingFields.length > 0) {
        throw new ApiError({
          code: 'ai.invalid_response',
          httpStatus: 502,
          i18nKey: 'errors.ai.invalid_response',
          retryable: true,
          details: { reason: 'business_apply_incomplete', missingFields },
        });
      }
      return result;
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(['business', vars.id], { business: data.business });
      void qc.invalidateQueries({ queryKey: ['businesses'] });
    },
  });
}

/**
 * `GET /api/v1/businesses/:id/impact` — Wave C2.
 *
 * Returns the lead + campaign counts attached to the business; used by the
 * DeleteBusinessModal cascade preview (US-118) and the editor footer.
 */
export function useBusinessImpact(
  id: string | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<BusinessImpact, ApiError> {
  const client = useApiClient();
  const enabled = options.enabled ?? Boolean(id);
  return useQuery<BusinessImpact, ApiError>({
    queryKey: ['business', id, 'impact'],
    queryFn: () =>
      getJson<BusinessImpact>(client, `/api/v1/businesses/${encodeURIComponent(id ?? '')}/impact`),
    enabled,
    retry: false,
  });
}
